import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { unifiedLogin, unifiedPhoneLogin, unifiedPhoneMpinLogin, requestOTP, resetMpinWithOtp, getTenantByCode, getLoginConfigByTenantCode, getTenantByDomain } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getFirstAvailableRoute } from '../utils/routeUtils';
import CustomLogin from './CustomLogin';

const UserLogin: React.FC = () => {
  const [tenantCode, setTenantCode] = useState<string | null>(null);
  const [loginConfig, setLoginConfig] = useState<any>(null);
  const [showConfigUi, setShowConfigUi] = useState(false); // true when tenant has domain (config UI); false → normal login UI
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [phoneAuthMode, setPhoneAuthMode] = useState<'otp' | 'mpin' | null>(null); // after phone+code: 'otp' = show OTP input, 'mpin' = show MPIN input
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mPin, setMPin] = useState('');
  const [showMPin, setShowMPin] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setTenantId] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState({
    code: '',
    email: '',
    phone: '',
    password: '',
    mPin: '',
  });
  const [forgotMpinMode, setForgotMpinMode] = useState<'idle' | 'confirm' | 'otpAndNew'>('idle');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewMpin, setForgotNewMpin] = useState('');
  const [forgotConfirmMpin, setForgotConfirmMpin] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isSystemAdmin, isTenantAdmin, hasPermission } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Resolve tenant: 1) by domain (hostname), 2) by tenantCode in URL (?tenantCode= or subdomain). If either yields config → config UI; else standard login.
  useEffect(() => {
    // Prefill from navigation state (e.g. after reset MPIN / change password)
    const state = (location.state || {}) as { phone?: string; email?: string; tenantCode?: string; loginMethod?: 'email' | 'phone' };
    if (state.phone) {
      setPhone(state.phone);
    }
    if (state.email) {
      setEmail(state.email);
    }
    if (state.tenantCode) {
      const codeUpper = state.tenantCode.trim().toUpperCase();
      setCode(codeUpper);
      setTenantCode(codeUpper);
    }
    if (state.loginMethod === 'phone') {
      setLoginMethod('phone');
      setPhoneAuthMode('mpin');
    }
    if (state.loginMethod === 'email') {
      setLoginMethod('email');
    }

    const hostname = window.location.hostname;

    // Build list of tenant-code candidates: ?tenantCode= first, then from hostname labels.
    // Supports short codes (KIMS) and longer codes (e.g. DHARMAHP) in any position: prefix, suffix, or middle.
    const getTenantCodeCandidates = (): string[] => {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('tenantCode');
      if (q) {
        const code = q.trim().toUpperCase();
        if (/^[A-Z0-9]+$/.test(code)) return [code];
      }
      const host = hostname.toLowerCase().replace(/^\.+|\.+$/g, '');
      const seen = new Set<string>();
      const RESERVED_WORDS = ['AMBULANCE'];
      const add = (c: string) => {
        if (!c || RESERVED_WORDS.includes(c) || seen.has(c)) return;
        if (/^[A-Z0-9]+$/.test(c) && c.length >= 2) {
          seen.add(c);
          candidates.push(c);
        }
      };
      const candidates: string[] = [];
      const parts = host.split('.').filter(Boolean);
      const MIN_CODE_LEN = 3;
      const MAX_CODE_LEN = 12; // e.g. DHARMAHP (8) and longer tenant codes
      for (const part of parts) {
        if (part === 'localhost' || part === 'www') continue;
        const cleaned = part.replace(/[^a-z0-9]/gi, '').toUpperCase();
        if (!cleaned) continue;
        add(cleaned);
        // Prefix/suffix for quick match (first and last 4, 3)
        if (cleaned.length >= 4) {
          add(cleaned.slice(0, 4));
          add(cleaned.slice(-4));
        }
        if (cleaned.length >= 3 && cleaned.length !== 4) {
          add(cleaned.slice(0, 3));
          add(cleaned.slice(-3));
        }
        // All substrings of length 4..MAX_CODE_LEN so KIMS, DHARMAHP etc. are found anywhere in the label.
        // Try longer lengths first to match longer tenant codes (e.g. DHARMAHP) with fewer API calls.
        for (let len = Math.min(MAX_CODE_LEN, cleaned.length); len >= 4; len--) {
          for (let i = 0; i <= cleaned.length - len; i++) {
            add(cleaned.slice(i, i + len));
          }
        }
      }
      return candidates;
    };

    // Hostnames that must always show standard login (no domain-based config UI)
    const SKIP_DOMAIN_CONFIG_HOSTNAMES = ['ambulance.binimise.com', 'www.ambulance.binimise.com'];

    const run = async () => {
      setLoadingConfig(true);
      try {
        // 1. First try by domain (hostname) → config UI if found (unless hostname is in skip list)
        const skipDomainCheck = hostname && SKIP_DOMAIN_CONFIG_HOSTNAMES.includes(hostname.toLowerCase());
        if (hostname && hostname !== 'localhost' && !skipDomainCheck) {
          try {
            const res = await getTenantByDomain(hostname);
            if (res.data.success && res.data.data?.tenantCode && res.data.data?.loginConfig) {
              setTenantCode(res.data.data.tenantCode);
              setLoginConfig(res.data.data.loginConfig);
              setShowConfigUi(true);
              setLoadingConfig(false);
              return;
            }
          } catch {
            // No tenant for this domain, fall through to tenant code
          }
        }

        // 2. Then try by tenantCode: candidates from ?tenantCode= or hostname labels (full + prefix/suffix so kims from kimsbbsr/bbsrkims)
        const candidates = getTenantCodeCandidates();
        let resolvedCode: string | null = null;
        let resolvedConfig: any = null;
        for (const code of candidates) {
          try {
            const response = await getLoginConfigByTenantCode(code);
            if (response.data.success && response.data.data) {
              const data = response.data.data;
              resolvedConfig = data.loginConfig ?? data;
              resolvedCode = code;
              break;
            }
          } catch {
            continue;
          }
        }
        if (resolvedCode && resolvedConfig) {
          setTenantCode(resolvedCode);
          setLoginConfig(resolvedConfig);
          setShowConfigUi(true);
        } else {
          setTenantCode(null);
          setLoginConfig(null);
          setShowConfigUi(false);
          // Pre-fill Tenant Code when detected from URL/hostname (e.g. kims.localhost → KIMS) so user doesn't have to type it
          if (candidates.length > 0) {
            setCode(candidates[0]);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch login config:', err);
        setLoginConfig(null);
        setShowConfigUi(false);
      } finally {
        setLoadingConfig(false);
      }
    };

    run();
  }, [location.state]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      if (isSystemAdmin) {
        // System admin always goes to tenants
        navigate('/dashboard/tenants', { replace: true });
      } else if (isTenantAdmin) {
        // Tenant admin always goes to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        // Normal users: navigate to first available route based on permissions
        const firstRoute = getFirstAvailableRoute(hasPermission, false, false);
        navigate(firstRoute, { replace: true });
      }
    }
  }, [isAuthenticated, isSystemAdmin, isTenantAdmin, hasPermission, navigate]);

  // Show custom login if config is available
  if (loadingConfig) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Show config UI only when tenant has domain (no domain / undefined / null → normal login UI)
  if (showConfigUi && tenantCode && loginConfig) {
    return <CustomLogin tenantCode={tenantCode} loginConfig={loginConfig} />;
  }

  const validateCode = (code: string): string => {
    if (!code) return 'Tenant Code is required';
    const codeRegex = /^[A-Z0-9]+$/;
    if (!codeRegex.test(code.toUpperCase())) {
      return 'Code must contain only uppercase letters and numbers';
    }
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return 'Phone is required';
    // Remove spaces, dashes, and parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const validateMPin = (v: string): string => {
    if (!v || !v.trim()) return 'MPIN is required';
    if (!/^[0-9]{4,6}$/.test(v.trim())) return 'MPIN must be 4 to 6 digits (numbers only)';
    return '';
  };

  const handleRequestOTP = async () => {
    setError('');
    setSuccess('');
    
    // Validate code first
    const codeError = validateCode(code);
    if (codeError) {
      setValidationErrors({ ...validationErrors, code: codeError });
      setError(codeError);
      return;
    }
    
    // Validate phone
    const phoneError = validatePhone(phone);
    if (phoneError) {
      setValidationErrors({ ...validationErrors, phone: phoneError });
      setError(phoneError);
      return;
    }
    
    setValidationErrors({ ...validationErrors, phone: '', code: '' });
    setPhoneAuthMode('otp');
    setLoading(true);

    try {
      // Fetch tenant info by code
      const codeUpper = code.trim().toUpperCase();
      const tenantResponse = await getTenantByCode(codeUpper);
      const tenant = tenantResponse.data.data;
      
      if (!tenant || !tenant._id) {
        setError('Invalid tenant code');
        setLoading(false);
        return;
      }

      setTenantId(tenant._id);
      
      // Request OTP with tenant context
      await requestOTP(phone, codeUpper);
      setOtpSent(true);
      setSuccess('OTP sent successfully! Check your phone.');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send OTP';
      const lowerMessage = errorMessage.toLowerCase();
      
      if (lowerMessage.includes('not found') || lowerMessage.includes('tenant not found') || lowerMessage.includes('invalid tenant')) {
        setError('Tenant not found. Please check your tenant code and try again.');
      } else {
        setError(errorMessage);
      }
      setTenantId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // CRITICAL: Always prevent default form submission to avoid page refresh
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent multiple submissions
    if (loading) {
      return false;
    }
    
    setError('');
    setSuccess('');

    // Validate code first
    const codeError = validateCode(code);
    if (codeError) {
      setValidationErrors({ ...validationErrors, code: codeError });
      setError(codeError);
      return false;
    }

    // Validate based on login method
    let hasErrors = false;
    const errors = { code: '', email: '', phone: '', password: '', mPin: '' };

    if (loginMethod === 'email') {
      errors.email = validateEmail(email);
      errors.password = validatePassword(password);
      if (errors.email || errors.password) hasErrors = true;
    } else if (loginMethod === 'phone') {
      errors.phone = validatePhone(phone);
      if (errors.phone) hasErrors = true;
      if (phoneAuthMode === 'otp') {
        if (!otp || otp.length !== 6) {
          setError('Please enter a valid 6-digit OTP');
          hasErrors = true;
        }
      } else if (phoneAuthMode === 'mpin') {
        errors.mPin = validateMPin(mPin);
        if (errors.mPin) hasErrors = true;
      } else {
        setError('Choose Request OTP or MPIN');
        hasErrors = true;
      }
    }

    setValidationErrors(errors);

    if (hasErrors) {
      if (errors.email) setError(errors.email);
      else if (errors.password) setError(errors.password);
      else if (errors.phone) setError(errors.phone);
      else if (errors.mPin) setError(errors.mPin);
      return false;
    }

    setLoading(true);

    try {
      // Fetch tenant info by code
      const codeUpper = code.trim().toUpperCase();
      let fetchedTenantId: string;
      
      try {
        const tenantResponse = await getTenantByCode(codeUpper);
        const tenant = tenantResponse.data.data;
        
        if (!tenant || !tenant._id) {
          setError('Tenant not found. Please check your tenant code and try again.');
        setLoading(false);
        return false;
      }
      
      fetchedTenantId = tenant._id;
      setTenantId(fetchedTenantId);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch tenant information';
        const lowerMessage = errorMessage.toLowerCase();
        
        if (lowerMessage.includes('not found') || lowerMessage.includes('tenant not found')) {
          setError('Tenant not found. Please check your tenant code and try again.');
        } else {
          setError('Failed to fetch tenant information. Please try again.');
        }
        setLoading(false);
        return false;
      }

      setTenantId(fetchedTenantId);

      let response;
      
      if (loginMethod === 'email') {
        response = await unifiedLogin(email, password, fetchedTenantId);
      } else if (loginMethod === 'phone') {
        if (phoneAuthMode === 'otp') {
          if (!otpSent) {
            setError('Please request OTP first');
            setLoading(false);
            return false;
          }
          response = await unifiedPhoneLogin(phone, otp, fetchedTenantId);
        } else {
          response = await unifiedPhoneMpinLogin(phone, mPin, fetchedTenantId);
        }
      }

      if (!response) return false;
      const { token, user } = response!.data.data;
      const userWithRoles = user as any;
      
      
      login(token, user);

      // Small delay to ensure state is updated before navigation
      await new Promise(resolve => setTimeout(resolve, 200));

      // Navigate based on user type
      const isSysAdmin = user.isSystem || false;
      const isTenAdmin =
        user.type === 'tenant_level_admin' ||
        user.type === 'tenant_admin' ||
        userWithRoles.roles?.includes('tenantAdmin') ||
        user.role === 'tenantAdmin' ||
        false;
      
      if (isSysAdmin) {
        // System admin always goes to tenants
        navigate('/dashboard/tenants');
      } else if (isTenAdmin) {
        // Tenant admin always goes to dashboard
        navigate('/dashboard');
      } else {
        // Normal users: navigate to first available route based on permissions
        const checkPermission = (permission: string): boolean => {
          return user.permissions?.some((p: any) => p.name === permission || p.id === permission) || false;
        };
        const firstRoute = getFirstAvailableRoute(checkPermission, false, false);
        navigate(firstRoute);
      }
    } catch (err: any) {
      // Provide clearer error messages with safe property access
      let errorMessage = 'Login failed. Please try again.';
      
      try {
        if (err && typeof err === 'object') {
          if (err.response && err.response.data && err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = err.message;
          }
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
      } catch (parseError) {
        // If error parsing fails, use default message
        console.error('Error parsing login error:', parseError);
      }
      
      // Display specific error messages from backend
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for specific error messages from backend
      if (lowerMessage.includes('email not found')) {
        setError('Email not found in this tenant. Please check your email and tenant code.');
      } else if (lowerMessage.includes('phone number not found')) {
        setError('Phone number not found in this tenant. Please check your phone number and tenant code.');
      } else if (lowerMessage.includes('invalid password')) {
        setError('Invalid password. Please check your password and try again.');
      } else if (lowerMessage.includes('invalid or inactive tenant') || lowerMessage.includes('tenant not found')) {
        setError('Invalid tenant code. Please check and try again.');
      } else if (lowerMessage.includes('invalid or expired otp')) {
        setError('Invalid or expired OTP. Please request a new OTP.');
      } else if (lowerMessage.includes('invalid mpin')) {
        setError('Invalid MPIN. Please check and try again.');
      } else if (lowerMessage.includes('mpin is not set')) {
        setError('MPIN is not set for this account. Use OTP or password to login.');
      } else if (lowerMessage.includes('account is not active')) {
        setError('Your account is not active. Please contact your administrator.');
      } else if (lowerMessage.includes('network') || lowerMessage.includes('timeout')) {
        setError('Network error. Please check your connection and try again.');
      } else if (lowerMessage.includes('invalid credentials')) {
        // Fallback for generic invalid credentials
        setError('Invalid email or password. Please check your credentials and try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
    
    return false; // Always return false to prevent form submission
  };

  return (
    <div
      className="login-container"
      style={{
        minHeight: '100vh',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div className="login-card" style={{ maxWidth: '500px' }}>
        <div className="login-header" style={{ textAlign: 'center', marginBottom: '15px' }}>
          <div style={{
            width: '70px',
            height: '70px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '45px',
            margin: '-5px auto 12px',
            boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
          }}>
            🚑
          </div>
          <h1 style={{ fontSize: '30px', fontWeight: '700', marginBottom: '8px', color: isDark ? '#f1f5f9' : '#1a1a1a' }}>
            Ambulance Tracking
          </h1>
          {/* <p style={{ fontSize: '17px', color: isDark ? '#cbd5e1' : '#666' }}>
            Sign in to your account
          </p> */}
        </div>

        {error && (
          <div className="error-message" style={{
            background: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee',
            border: `2px solid ${isDark ? '#ef4444' : '#e74c3c'}`,
            color: isDark ? '#fca5a5' : '#c33',
            padding: '14px 18px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '17px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: isDark ? '0 2px 8px rgba(239, 68, 68, 0.3)' : '0 2px 8px rgba(231, 76, 60, 0.2)'
          }}>
            <span style={{ fontSize: '22px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="success-message" style={{
            background: '#e8f5e9',
            border: '1px solid #4caf50',
            color: '#2e7d32',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '16px'
          }}>
            {success}
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
              type="button"
              onClick={() => {
                setLoginMethod('email');
                setError('');
                setSuccess('');
              }}
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '17px',
                fontWeight: '600',
                background: loginMethod === 'email' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : (isDark ? '#1e293b' : 'white'),
                color: loginMethod === 'email' ? 'white' : (isDark ? '#cbd5e1' : '#666'),
                border: loginMethod === 'email' ? 'none' : `2px solid ${isDark ? '#334155' : '#e0e0e0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: loginMethod === 'email' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📧</span>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Email</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginMethod('phone');
                setError('');
                setSuccess('');
                setPhoneAuthMode('mpin');
                setOtpSent(false);
                setOtp('');
                setMPin('');
              }}
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '17px',
                fontWeight: '600',
                background: loginMethod === 'phone' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : (isDark ? '#1e293b' : 'white'),
                color: loginMethod === 'phone' ? 'white' : (isDark ? '#cbd5e1' : '#666'),
                border: loginMethod === 'phone' ? 'none' : `2px solid ${isDark ? '#334155' : '#e0e0e0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: loginMethod === 'phone' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📱</span>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Phone</span>
            </button>
          </div>
        </div>

        <form 
          onSubmit={handleSubmit} 
          noValidate 
          action="#" 
          method="post"
          onKeyDown={(e) => {
            // Prevent form submission on Enter key if there are errors
            if (e.key === 'Enter' && (loading || error)) {
              e.preventDefault();
            }
          }}
        >
          {loginMethod === 'email' && (
            <>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="email" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: isDark ? '#f1f5f9' : '#333'
                }}>
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setValidationErrors({ ...validationErrors, email: '' });
                  }}
                  placeholder="Enter your email"
                  required
                  autoFocus
                  className={validationErrors.email ? 'input-error' : ''}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '17px',
                    border: `2px solid ${validationErrors.email ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: validationErrors.email ? (isDark ? 'rgba(239, 68, 68, 0.1)' : '#fff5f5') : (isDark ? '#0f172a' : 'white'),
                    color: isDark ? '#f1f5f9' : '#1e293b'
                  }}
                  onFocus={(e) => e.target.style.borderColor = validationErrors.email ? '#e74c3c' : '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = validationErrors.email ? '#e74c3c' : '#e0e0e0'}
                />
                {validationErrors.email && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    {validationErrors.email}
                  </small>
                )}
                {!validationErrors.email && (
                  <small style={{ color: isDark ? '#64748b' : '#999', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    Works for both users and tenant admins
                  </small>
                )}
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="code" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: isDark ? '#f1f5f9' : '#333'
                }}>
                  Tenant Code
                </label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    // Only allow uppercase letters and numbers, convert to uppercase
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setCode(value);
                    setValidationErrors({ ...validationErrors, code: '' });
                    setTenantId(null); // Reset tenantId when code changes
                  }}
                  placeholder="Enter tenant code"
                  required
                  className={validationErrors.code ? 'input-error' : ''}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '17px',
                    border: `2px solid ${validationErrors.code ? '#e74c3c' : '#e0e0e0'}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: validationErrors.code ? '#fff5f5' : 'white',
                    color: '#1e293b',
                    textTransform: 'uppercase'
                  }}
                  onFocus={(e) => e.target.style.borderColor = validationErrors.code ? '#e74c3c' : '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = validationErrors.code ? '#e74c3c' : '#e0e0e0'}
                />
                {validationErrors.code && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    {validationErrors.code}
                  </small>
                )}
                {/* {!validationErrors.code && (
                  <small style={{ color: '#999', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    Only uppercase letters and numbers allowed
                  </small>
                )} */}
              </div>
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label htmlFor="password" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: isDark ? '#f1f5f9' : '#333'
                }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setValidationErrors({ ...validationErrors, password: '' });
                    }}
                    placeholder="Enter your password"
                    required
                    className={validationErrors.password ? 'input-error' : ''}
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 16px',
                      fontSize: '17px',
                      border: `2px solid ${validationErrors.password ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}`,
                      borderRadius: '8px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      backgroundColor: validationErrors.password ? (isDark ? 'rgba(239, 68, 68, 0.1)' : '#fff5f5') : (isDark ? '#0f172a' : 'white'),
                      color: isDark ? '#f1f5f9' : '#1e293b'
                    }}
                    onFocus={(e) => e.target.style.borderColor = validationErrors.password ? '#e74c3c' : '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = validationErrors.password ? '#e74c3c' : '#e0e0e0'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '22px',
                      padding: '4px',
                      color: isDark ? '#64748b' : '#999'
                    }}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {validationErrors.password && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    {validationErrors.password}
                  </small>
                )}
              </div>
              <div style={{ marginTop: '-8px', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/change-password', {
                      state: {
                        email,
                        tenantCode: code,
                      },
                    });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    color: '#818cf8',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Change Password?
                </button>
              </div>
            </>
          )}

          {loginMethod === 'phone' && (
            <>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="phone" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: isDark ? '#f1f5f9' : '#333'
                }}>
                  Phone Number
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPhone(numeric);
                    setValidationErrors({ ...validationErrors, phone: '' });
                  }}
                  placeholder="1234567890"
                  required
                  autoFocus
                  className={validationErrors.phone ? 'input-error' : ''}
                  maxLength={10}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '17px',
                    border: `2px solid ${validationErrors.phone ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: validationErrors.phone ? (isDark ? 'rgba(239, 68, 68, 0.1)' : '#fff5f5') : (isDark ? '#0f172a' : 'white'),
                    color: isDark ? '#f1f5f9' : '#1e293b'
                  }}
                  onFocus={(e) => e.target.style.borderColor = validationErrors.phone ? '#e74c3c' : '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = validationErrors.phone ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}
                />
                {validationErrors.phone && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    {validationErrors.phone}
                  </small>
                )}
                {!validationErrors.phone && (
                  <small style={{ color: isDark ? '#64748b' : '#999', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    Works for both users and tenant admins
                  </small>
                )}
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="code-phone" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: isDark ? '#f1f5f9' : '#333'
                }}>
                  Tenant Code
                </label>
                <input
                  id="code-phone"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    // Only allow uppercase letters and numbers, convert to uppercase
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setCode(value);
                    setValidationErrors({ ...validationErrors, code: '' });
                    setTenantId(null); // Reset tenantId when code changes
                  }}
                  placeholder="Enter tenant code"
                  required
                  className={validationErrors.code ? 'input-error' : ''}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '17px',
                    border: `2px solid ${validationErrors.code ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: validationErrors.code ? (isDark ? 'rgba(239, 68, 68, 0.1)' : '#fff5f5') : (isDark ? '#0f172a' : 'white'),
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    textTransform: 'uppercase'
                  }}
                  onFocus={(e) => e.target.style.borderColor = validationErrors.code ? '#e74c3c' : '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = validationErrors.code ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}
                />
                {validationErrors.code && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    {validationErrors.code}
                  </small>
                )}
                {/* {!validationErrors.code && (
                  <small style={{ color: isDark ? '#64748b' : '#999', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                    Only uppercase letters and numbers allowed
                  </small>
                )} */}
              </div>
              {phoneAuthMode === 'otp' && (
                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <label htmlFor="otp" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '16px', color: isDark ? '#f1f5f9' : '#333' }}>
                    OTP Code
                  </label>
                  <input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    required
                    maxLength={6}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '17px',
                      border: `2px solid ${isDark ? '#334155' : '#e0e0e0'}`,
                      borderRadius: '8px',
                      outline: 'none',
                      letterSpacing: '4px',
                      textAlign: 'center',
                      backgroundColor: isDark ? '#0f172a' : 'white',
                      color: isDark ? '#f1f5f9' : '#1e293b'
                    }}
                  />
                </div>
              )}
              {phoneAuthMode === 'mpin' && (
                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <label htmlFor="mpin">MPIN</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="mpin"
                      type={showMPin ? 'text' : 'password'}
                      inputMode="numeric"
                      autoComplete="off"
                      value={mPin}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setMPin(digits);
                        setValidationErrors({ ...validationErrors, mPin: '' });
                      }}
                      placeholder="4–6 digit MPIN"
                      required
                      maxLength={6}
                      className={validationErrors.mPin ? 'input-error' : ''}
                      style={{
                        width: '100%',
                        padding: '12px 48px 12px 16px',
                        fontSize: '17px',
                        border: `2px solid ${validationErrors.mPin ? '#e74c3c' : (isDark ? '#334155' : '#e0e0e0')}`,
                        borderRadius: '8px',
                        outline: 'none',
                        backgroundColor: isDark ? '#0f172a' : 'white',
                        color: isDark ? '#f1f5f9' : '#1e293b'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowMPin(!showMPin)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '22px',
                        padding: '4px',
                        color: isDark ? '#64748b' : '#999'
                      }}
                      title={showMPin ? 'Hide MPIN' : 'Show MPIN'}
                    >
                      {showMPin ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.mPin && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>{validationErrors.mPin}</small>
                  )}
                </div>
              )}
              {phoneAuthMode === 'mpin' && (
                <div style={{ marginTop: '-8px', marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/reset-mpin', {
                        state: {
                          phone,
                          tenantCode: code,
                        },
                      });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      color: '#818cf8',
                      fontSize: '14px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Forgot MPIN?
                  </button>
                </div>
              )}

              {forgotMpinMode !== 'idle' && (
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    background: isDark ? 'rgba(15,23,42,0.9)' : '#f8fafc',
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', color: isDark ? '#e2e8f0' : '#0f172a' }}>
                    Reset MPIN
                  </div>
                  {forgotMpinMode === 'confirm' && (
                    <>
                      <p style={{ fontSize: '13px', marginBottom: '10px', color: isDark ? '#94a3b8' : '#475569' }}>
                        Confirm your phone number and tenant code to receive an OTP.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => {
                            const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setPhone(numeric);
                          }}
                          placeholder="Phone number"
                          maxLength={10}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                            borderRadius: '6px',
                            background: isDark ? '#020617' : 'white',
                            color: isDark ? '#e2e8f0' : '#0f172a',
                          }}
                        />
                        <input
                          type="text"
                          value={code}
                          onChange={() => {}}
                          readOnly
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
                            borderRadius: '6px',
                            background: isDark ? '#1e293b' : '#e2e8f0',
                            textTransform: 'uppercase',
                            color: isDark ? '#e2e8f0' : '#0f172a',
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={async () => {
                            setError('');
                            setSuccess('');
                            const phoneError = validatePhone(phone);
                            if (phoneError) {
                              setValidationErrors({ ...validationErrors, phone: phoneError });
                              setError(phoneError);
                              return;
                            }
                            const codeError = validateCode(code);
                            if (codeError) {
                              setValidationErrors({ ...validationErrors, code: codeError });
                              setError(codeError);
                              return;
                            }
                            try {
                              setLoading(true);
                              await requestOTP(phone, code);
                              setSuccess('OTP sent successfully to your phone');
                              setForgotMpinMode('otpAndNew');
                            } catch (err: any) {
                              const errorMessage = err.response?.data?.message || 'Failed to send OTP';
                              setError(errorMessage);
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading || !phone || !code}
                          style={{
                            flex: 1,
                            padding: '10px 12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            background: (loading || !phone || !code) ? '#4b5563' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (loading || !phone || !code) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {loading ? 'Sending...' : 'Send OTP'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setForgotMpinMode('idle');
                            setForgotOtp('');
                            setForgotNewMpin('');
                            setForgotConfirmMpin('');
                          }}
                          style={{
                            padding: '10px 12px',
                            fontSize: '14px',
                            background: 'transparent',
                            borderRadius: '6px',
                            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                            color: isDark ? '#cbd5e1' : '#64748b',
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                  {forgotMpinMode === 'otpAndNew' && (
                    <>
                      <p style={{ fontSize: '13px', marginBottom: '10px', color: isDark ? '#94a3b8' : '#475569' }}>
                        Enter the OTP sent to your phone and choose a new MPIN.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        <input
                          type="text"
                          value={forgotOtp}
                          onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="6-digit OTP"
                          maxLength={6}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                            borderRadius: '6px',
                            letterSpacing: '4px',
                            textAlign: 'center',
                            background: isDark ? '#020617' : 'white',
                            color: isDark ? '#e2e8f0' : '#0f172a',
                          }}
                        />
                        <input
                          type="password"
                          value={forgotNewMpin}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setForgotNewMpin(digits);
                          }}
                          placeholder="New 4–6 digit MPIN"
                          maxLength={6}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                            borderRadius: '6px',
                            background: isDark ? '#020617' : 'white',
                            color: isDark ? '#e2e8f0' : '#0f172a',
                          }}
                        />
                        <input
                          type="password"
                          value={forgotConfirmMpin}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setForgotConfirmMpin(digits);
                          }}
                          placeholder="Confirm new MPIN"
                          maxLength={6}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                            borderRadius: '6px',
                            background: isDark ? '#020617' : 'white',
                            color: isDark ? '#e2e8f0' : '#0f172a',
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={async () => {
                            setError('');
                            setSuccess('');
                            if (!forgotOtp || forgotOtp.length !== 6) {
                              setError('Please enter a valid 6-digit OTP');
                              return;
                            }
                            if (!forgotNewMpin || forgotNewMpin.length < 4) {
                              setError('New MPIN must be 4 to 6 digits');
                              return;
                            }
                            if (forgotNewMpin !== forgotConfirmMpin) {
                              setError('New MPIN and confirm MPIN do not match');
                              return;
                            }
                            try {
                              setLoading(true);
                              await resetMpinWithOtp(phone, code, forgotOtp, forgotNewMpin);
                              setSuccess('MPIN updated successfully. Please login with your new MPIN.');
                              setForgotMpinMode('idle');
                              setForgotOtp('');
                              setForgotNewMpin('');
                              setForgotConfirmMpin('');
                              setPhoneAuthMode('mpin');
                            } catch (err: any) {
                              const errorMessage = err.response?.data?.message || 'Failed to reset MPIN';
                              setError(errorMessage);
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                          style={{
                            flex: 1,
                            padding: '10px 12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            background: loading ? '#4b5563' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {loading ? 'Saving...' : 'Save New MPIN'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setForgotMpinMode('confirm');
                            setForgotOtp('');
                            setForgotNewMpin('');
                            setForgotConfirmMpin('');
                          }}
                          style={{
                            padding: '10px 12px',
                            fontSize: '14px',
                            background: 'transparent',
                            borderRadius: '6px',
                            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                            color: isDark ? '#cbd5e1' : '#64748b',
                            cursor: 'pointer',
                          }}
                        >
                          Back
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {(loginMethod === 'email' || (loginMethod === 'phone' && (phoneAuthMode === 'otp' || phoneAuthMode === 'mpin'))) && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
              <button 
                type="submit" 
                disabled={loading}
                onClick={(e) => {
                  if (loading) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  fontSize: '18px',
                  fontWeight: '600',
                  background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                {loading ? 'Logging in...' : 'Login to Dashboard'}
              </button>
            </div>
          )}
        </form>

        {/* <div className="login-link" style={{ 
          textAlign: 'center', 
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: `1px solid ${isDark ? '#334155' : '#e0e0e0'}`
        }}>
          <Link to="/system" style={{ 
            color: isDark ? '#818cf8' : '#667eea', 
            textDecoration: 'none',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            System Admin? Login here →
          </Link>
        </div> */}
      </div>
    </div>
  );
};

export default UserLogin;

